import { useForm } from "@tanstack/react-form";
import { useEffect, useId } from "react";

import { t } from "../../../i18n";
import { editSettlementSchema } from "../../../lib/schemas";
import { trpc } from "../../../lib/trpc";
import { fieldError } from "../../../lib/validation";
import { Button, Dialog, Field, FieldMessage, Input, Stack } from "../../ui";
import type { Settlement } from "../types";

interface EditSettlementDialogProps {
  settlement: Settlement;
  open: boolean;
  onClose: () => void;
}

export function EditSettlementDialog({ settlement, open, onClose }: EditSettlementDialogProps) {
  const utils = trpc.useUtils();
  const update = trpc.settlement.update.useMutation({
    onSuccess: () => {
      utils.settlement.get.invalidate({ settlementId: settlement.id });
      utils.settlement.list.invalidate({ groupId: settlement.householdId });
      onClose();
    },
  });

  const form = useForm({
    defaultValues: {
      title: settlement.title,
      startDate: settlement.startDate,
      endDate: settlement.endDate,
    },
    validators: {
      onChange: editSettlementSchema,
      onSubmit: editSettlementSchema,
    },
    onSubmit: ({ value }) => {
      const trimmed = value.title.trim();
      const unchanged =
        trimmed === settlement.title &&
        value.startDate === settlement.startDate &&
        value.endDate === settlement.endDate;
      if (unchanged) {
        onClose();
        return;
      }
      update.mutate({
        settlementId: settlement.id,
        title: trimmed,
        startDate: value.startDate,
        endDate: value.endDate,
      });
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      title: settlement.title,
      startDate: settlement.startDate,
      endDate: settlement.endDate,
    });
  }, [open, settlement.title, settlement.startDate, settlement.endDate, form]);

  const formId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.settlement.editDialog.title}
      description={t.settlement.editDialog.description}
      footer={
        <>
          <Button onClick={onClose}>{t.common.cancel}</Button>
          <Button type="submit" form={formId} variant="primary" isLoading={update.isPending}>
            {t.common.save}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Stack gap={4}>
          <form.Field name="title">
            {(field) => (
              <Field label={t.settlement.editDialog.titleLabel} required error={fieldError(field)}>
                <Input
                  autoFocus
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t.settlement.editDialog.titlePlaceholder}
                  invalid={!!fieldError(field)}
                />
              </Field>
            )}
          </form.Field>
          <div className="grid grid-cols-2 gap-3">
            <form.Field name="startDate">
              {(field) => (
                <Field
                  label={t.settlement.editDialog.startDateLabel}
                  required
                  error={fieldError(field)}
                >
                  <Input
                    type="date"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    invalid={!!fieldError(field)}
                  />
                </Field>
              )}
            </form.Field>
            <form.Field name="endDate">
              {(field) => (
                <Field
                  label={t.settlement.editDialog.endDateLabel}
                  required
                  error={fieldError(field)}
                >
                  <Input
                    type="date"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    invalid={!!fieldError(field)}
                  />
                </Field>
              )}
            </form.Field>
          </div>
          {update.isError && (
            <FieldMessage tone="error" className="text-sm">
              {update.error.message}
            </FieldMessage>
          )}
        </Stack>
      </form>
    </Dialog>
  );
}
